// EditRoot.tsx
import React from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import AnimateTopBar from "./AnimateTopBar";
import LeftPanel from "./LeftPanel";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import { SpriteSheetCalculation } from "../utils/calculateSpriteSheet";
import BottomBar from "./BottomBar";
import RightPanel from "./RightPanel";
import MainAnimation from "./MainAnimation";
import { AnimateActionType } from "../hooks/useAnimateActions";
import GlobalMessage from "../../pricing/GlobalMessage";

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.background};
    overflow: hidden;
    gap: 0;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    flex: 1; /* Take remaining vertical space */
    min-height: 0; /* Allow shrinking */
    height: auto;
    gap: 0;
    padding: 0;
    margin: 0;
`;

const Main = styled.div< { $isPlaceholder: boolean } >`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    max-width: 100%;
    height: 100%;
    opacity: ${props => props.$isPlaceholder ? 0.6 : 1};
    padding: 10px;
    gap: 10px;
    box-sizing: border-box;
`;

const AnimationContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    flex: 1;
    min-height: 0;
    height: 100%;
    object-fit: contain;
    overflow: hidden;
    gap: 10px;

    @media (max-width: 768px) {
        flex-direction: column-reverse;
        align-items: center;
        overflow-y: auto;
    }
`;

interface EditRootProps {
    config: any;
    isLoading: boolean;
    animationId: string;
    initialName: string;
    spriteSheetDefaults: SpriteSheetCalculation;
    animationImages: string[];
    currentAction: AnimateActionType | null;
    imageUrl: string;
    userPrompt: string;
    handleImageChange: (url: string) => void;
    handleImageError: (err: string) => void;
    handleUserPromptChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleAnimate: () => void;
    removeBackground: boolean;
    handleRemoveBackgroundChange: (val: boolean) => void;
    isLeftPanelOpen: boolean;
    setIsLeftPanelOpen: (val: boolean) => void;
    animationSpeed: number;
    handleAnimationSpeedChange: (val: number) => void;
    frameWidth: number | string;
    frameHeight: number | string;
    handleFrameWidthChange: (val: string | number) => void;
    handleFrameHeightChange: (val: string | number) => void;
    loop: boolean;
    handleLoopChange: (val: boolean) => void;
    isMobile: boolean;
    maxFrames: number;
    handleNumFramesChange: (val: number) => void;
    handleDuplicateCurrentFrame: () => void;
    handleRemoveVideoBackground: () => void;
    handleCropTransparentPixels: () => void;
    handleReorder: (newOrder: string[]) => void;
    startFrame: number;
    endFrame: number;
    handleRangeChange: (start: number, end: number) => void;
    hiddenFrames: number[];
    handleToggleFrameVisibility: () => void;
    currentFrameIndex: number;
    currentFrameNumber: number;
    displayMaxFrames: number;
    isPlaying: boolean;
    setIsPlaying: (val: boolean) => void;
    showOverlay: boolean;
    overlayIcon: "play" | "pause";
    handlePlayPauseClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
    handleSeek: (val: number) => void;
    onFrameClick: (index: number) => void;
    handleExport: (formatId: string, settings: any) => void;
    progress: number;
    message: string;
    handleRemix: () => void;
    zoomOut: boolean;
    handleZoomOutChange: (val: boolean) => void;
    isProcessingImage: boolean;
    handleGenerateImage: (prompt: string) => void;
    handleAnimationNameChange: (newName: string) => void;
    visibility: 'public' | 'private';
    onVisibilityChange: (newVisibility: 'public' | 'private') => void;
    enhancePrompt: boolean;
    handleEnhancePromptChange: (newValue: boolean) => void;
}

const EditRoot = ({
    config,
    isLoading,
    animationId,
    initialName,
    spriteSheetDefaults,
    animationImages,
    currentAction,
    imageUrl,
    userPrompt,
    handleImageChange,
    handleImageError,
    handleUserPromptChange,
    handleAnimate,
    removeBackground,
    handleRemoveBackgroundChange,
    isLeftPanelOpen,
    setIsLeftPanelOpen,
    animationSpeed,
    handleAnimationSpeedChange,
    frameWidth,
    frameHeight,
    handleFrameWidthChange,
    handleFrameHeightChange,
    loop,
    handleLoopChange,
    isMobile,
    maxFrames,
    handleNumFramesChange,
    handleDuplicateCurrentFrame,
    handleRemoveVideoBackground,
    handleCropTransparentPixels,
    handleReorder,
    startFrame,
    endFrame,
    handleRangeChange,
    hiddenFrames,
    handleToggleFrameVisibility,
    currentFrameIndex,
    currentFrameNumber,
    displayMaxFrames,
    isPlaying,
    setIsPlaying,
    showOverlay,
    overlayIcon,
    handlePlayPauseClick,
    handleSeek,
    onFrameClick,
    handleExport,
    progress,
    message,
    handleRemix,
    zoomOut,
    handleZoomOutChange,
    isProcessingImage,
    handleGenerateImage,
    handleAnimationNameChange,
    visibility,
    onVisibilityChange,
    enhancePrompt,
    handleEnhancePromptChange,
}: EditRootProps) => {

    return (
        <Root>
            <GlobalMessage config={config} colorMode="dark" />
            <AnimateTopBar 
                animationId={animationId} 
                initialName={initialName} 
                spriteSheetDefaults={spriteSheetDefaults}
                totalFrames={animationImages.length}
                currentAction={currentAction}
                onExport={handleExport}
                progress={progress}
                message={message}
                onRemixClick={handleRemix}
                handleAnimationNameChange={handleAnimationNameChange}
                visibility={visibility}
                onVisibilityChange={onVisibilityChange}
            />
            <Inner id="inner">
                <LeftPanel
                    config={config}
                    imageUrl={imageUrl}
                    descriptionText={userPrompt}
                    handleImageChange={handleImageChange}
                    handleImageError={handleImageError}
                    handleDescriptionChange={handleUserPromptChange}
                    onAnimateClick={handleAnimate}
                    currentAction={currentAction}
                    animationImages={animationImages}
                    removeBackground={removeBackground}
                    handleRemoveBackgroundChange={handleRemoveBackgroundChange}
                    open={isLeftPanelOpen}
                    onOpenChange={setIsLeftPanelOpen}
                    animationSpeed={animationSpeed}
                    handleAnimationSpeedChange={handleAnimationSpeedChange}
                    frameWidth={frameWidth}
                    frameHeight={frameHeight}
                    handleFrameWidthChange={handleFrameWidthChange}
                    handleFrameHeightChange={handleFrameHeightChange}
                    loop={loop}
                    handleLoopChange={handleLoopChange}
                    progress={progress}
                    message={message}
                    zoomOut={zoomOut}
                    handleZoomOutChange={handleZoomOutChange}
                    isProcessingImage={isProcessingImage}
                    isMobile={isMobile}
                    handleGenerateImage={handleGenerateImage}
                    enhancePrompt={enhancePrompt}
                    handleEnhancePromptChange={handleEnhancePromptChange}
                />
                <Main $isPlaceholder={animationImages.length === 0}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <AnimationContainer>
                                <MainAnimation 
                                    animationImages={animationImages}
                                    currentFrameIndex={currentFrameIndex}
                                    startFrame={startFrame}
                                    endFrame={endFrame}
                                    hiddenFrames={hiddenFrames}
                                    currentAction={currentAction}
                                    overlayIcon={overlayIcon}
                                    showOverlay={showOverlay}
                                    handlePlayPauseClick={handlePlayPauseClick}
                                />

                                <RightPanel 
                                    isMobile={isMobile}
                                    animationSpeed={animationSpeed}
                                    handleAnimationSpeedChange={handleAnimationSpeedChange}
                                    frameWidth={frameWidth}
                                    frameHeight={frameHeight}
                                    handleFrameWidthChange={handleFrameWidthChange}
                                    handleFrameHeightChange={handleFrameHeightChange}
                                    currentFrameNumber={currentFrameNumber}
                                    maxFrames={displayMaxFrames}
                                    isPlacholder={animationImages.length === 0}
                                    isPlaying={isPlaying}
                                    handleDuplicateCurrentFrame={handleDuplicateCurrentFrame}
                                    handleRemoveVideoBackground={handleRemoveVideoBackground}
                                    handleCropTransparentPixels={handleCropTransparentPixels}
                                    currentAction={currentAction}
                                    isFrameHidden={hiddenFrames.includes(currentFrameIndex)}
                                    handleToggleFrameVisibility={handleToggleFrameVisibility}
                                    progress={progress}
                                    message={message}
                                />
                            </AnimationContainer>

                            <BottomBar
                                orderedImages={animationImages}
                                currentFrameIndex={currentFrameIndex}
                                numOfFrames={animationImages.length > 0 ? animationImages.length : maxFrames}
                                startFrame={startFrame}
                                endFrame={endFrame}
                                handleRangeChange={handleRangeChange}
                                handleReorder={handleReorder}
                                onFrameClick={onFrameClick}
                                setIsPlaying={setIsPlaying}
                                handleFrameChange={handleSeek}
                                isPlaying={isPlaying}
                                handlePlayPauseClick={handlePlayPauseClick}
                                isPlaceholder={animationImages.length === 0}
                                isMobile={isMobile}
                                hiddenFrames={hiddenFrames}
                            />
                        </>
                    )}
                </Main>
            </Inner>
        </Root>
    );
};

export default EditRoot;